
import React, { useState, useEffect } from 'react';
import { PopupContent } from '@/types/popup-system';

interface ClassifiedDocumentPopupProps {
  content: PopupContent;
  onDismiss: () => void;
  onAction?: (action: string) => void;
}

// Simulates redacted text that reveals on hover
const RedactedText = ({ children }: { children: string }) => {
  const [revealed, setRevealed] = useState(false);
  
  // Randomly decide if this text should be redacted
  const shouldRedact = children.length > 5 && Math.random() > 0.7;
  
  if (!shouldRedact) {
    return <span>{children}</span>;
  }
  
  return (
    <span
      className={`relative cursor-pointer transition-all duration-300 ${
        revealed 
          ? 'bg-transparent text-amber-100' 
          : 'bg-black text-transparent hover:bg-amber-900/50 hover:text-amber-100/50'
      }`}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      title="HOVER TO DECLASSIFY"
    >
      {children}
    </span>
  );
};

export const ClassifiedDocumentPopup = ({ content, onDismiss, onAction }: ClassifiedDocumentPopupProps) => {
  const [stampVisible, setStampVisible] = useState(false);
  const [scanlinePos, setScanlinePos] = useState(0);
  
  // Generate document metadata
  const docId = `WB-${Math.floor(Math.random() * 9999).toString().padStart(4, '0')}-${String.fromCharCode(65 + Math.floor(Math.random() * 26))}`;
  const clearanceLevel = ['ALPHA', 'BETA', 'GAMMA', 'DELTA', 'OMEGA'][Math.floor(Math.random() * 5)];
  const securityCode = `SEC-${Math.floor(Math.random() * 999).toString().padStart(3, '0')}`;
  
  useEffect(() => {
    // Delayed stamp animation
    const stampTimer = setTimeout(() => setStampVisible(true), 500);
    
    // Scanning effect
    const scanTimer = setInterval(() => {
      setScanlinePos(prev => (prev + 1) % 100);
    }, 50);
    
    return () => {
      clearTimeout(stampTimer);
      clearInterval(scanTimer);
    };
  }, []);

  // Process message to add redactions
  const processMessage = (text: string) => {
    const words = text.split(' ');
    return words.map((word, i) => (
      <React.Fragment key={i}>
        <RedactedText>{word}</RedactedText>
        {i < words.length - 1 ? ' ' : ''}
      </React.Fragment>
    ));
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90">
      {/* Scanline effect */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-10"
        style={{
          background: `linear-gradient(transparent ${scanlinePos}%, rgba(255,200,100,0.1) ${scanlinePos + 1}%, transparent ${scanlinePos + 2}%)`
        }}
      />
      
      <div className="bg-amber-950/95 border-2 border-amber-700/50 max-w-lg w-full mx-4 relative shadow-2xl">
        {/* Document texture overlay */}
        <div 
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        
        {/* Classification banner */}
        <div className="bg-red-900 text-red-100 px-4 py-1 text-center text-xs font-mono tracking-widest border-b border-red-700">
          ▓▓▓ CLASSIFIED DOCUMENT ▓▓▓
        </div>
        
        {/* Document header */}
        <div className="bg-amber-900/50 px-4 py-3 border-b border-amber-700/30 relative">
          <div className="flex justify-between items-start">
            <div className="font-mono text-xs text-amber-400/70 space-y-1">
              <div>DOC ID: {docId}</div>
              <div>CLEARANCE: LEVEL {clearanceLevel}</div>
              <div>CODE: {securityCode}</div>
            </div>
            <div className="text-right font-mono text-xs text-amber-400/70">
              <div>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })}</div>
              <div>{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          </div>
          
          {/* Classification stamp */}
          <div 
            className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-12 transition-all duration-500 ${
              stampVisible ? 'opacity-30 scale-100' : 'opacity-0 scale-150'
            }`}
          >
            <div className="border-4 border-red-600 text-red-600 px-4 py-2 font-bold text-xl tracking-widest">
              CLASSIFIED
            </div>
          </div>
        </div>
        
        {/* Document title */}
        <div className="px-4 py-3 border-b border-amber-700/30">
          <h2 className="text-amber-100 font-bold text-lg tracking-wide font-mono uppercase">
            RE: {content.title}
          </h2>
        </div>
        
        {/* Document body */}
        <div className="p-4 font-mono text-sm">
          <div className="bg-black/30 p-4 border border-amber-700/30 text-amber-100/90 leading-relaxed">
            <p className="whitespace-pre-wrap">
              {processMessage(content.message)}
            </p>
          </div>
          
          <div className="mt-3 text-amber-500/50 text-xs italic">
            * Hover over redacted sections to declassify
          </div>
        </div>
        
        {/* Document footer */}
        <div className="px-4 py-3 border-t border-amber-700/30 flex justify-between items-center">
          <div className="text-xs text-amber-500/50 font-mono">
            WEIRDOBEACH ARCHIVES
          </div>
          
          <div className="flex gap-2">
            {content.actionButtons?.map((button, index) => (
              <button
                key={index}
                onClick={() => onAction?.(button.action)}
                className="bg-amber-900/50 border border-amber-600/50 text-amber-200 px-4 py-1.5 text-xs font-mono hover:bg-amber-800/50 transition-colors"
              >
                {button.text}
              </button>
            ))}
            {content.dismissible && (
              <button
                onClick={onDismiss}
                className="bg-red-900/50 border border-red-600/50 text-red-200 px-4 py-1.5 text-xs font-mono hover:bg-red-800/50 transition-colors"
              >
                [ARCHIVE]
              </button>
            )}
          </div>
        </div>
        
        {/* Bottom classification banner */}
        <div className="bg-red-900 text-red-100 px-4 py-1 text-center text-xs font-mono tracking-widest">
          ▓▓▓ UNAUTHORIZED ACCESS PROHIBITED ▓▓▓
        </div>
      </div>
    </div>
  );
};
